import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { initializeApi } from '@/lib/api';
import { STORAGE_KEYS } from '@/config/api';

// 用户信息接口：name 来自 tokenObj.loginIn，无 avatar/department；role 可选（用于权限校验）
export interface User {
  id?: string;
  name: string;
  email?: string;
  role?: string;
  lastLogin?: string;
}

// 认证状态接口
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// 认证上下文接口
interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  setUser: (user: User | null) => void;
}

// 创建认证上下文
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 认证提供者组件
export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true
  });

  // 从 localStorage 的 tokenObj 读取 loginIn 作为显示名
  const getDisplayNameFromToken = (): string => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.TOKEN_OBJ);
      if (!raw) return '';
      const tokenObj = JSON.parse(raw) as { loginIn?: string };
      return typeof tokenObj?.loginIn === 'string' ? tokenObj.loginIn : '';
    } catch {
      return '';
    }
  };

  // 初始化时检查本地存储的认证信息
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const storedUser = localStorage.getItem('auth_user');
        const displayName = getDisplayNameFromToken();

        if (token) {
          initializeApi(token);
          const parsed = storedUser ? (JSON.parse(storedUser) as Partial<User>) : null;
          const user: User = parsed
            ? { ...parsed, name: displayName || parsed.name || '用户' }
            : { name: displayName || '用户' };
          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false
          });
        } else {
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false
          });
        }
      } catch (error) {
        console.error('初始化认证状态失败:', error);
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    };

    initializeAuth();
  }, []);

  // 模拟登录方法
  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      // 模拟API调用延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const displayName = getDisplayNameFromToken();
      const mockUser: User = {
        id: '1',
        name: displayName || '测试用户',
        email: email,
        lastLogin: new Date().toISOString()
      };

      const token = localStorage.getItem('access_token');
      if (token) {
        // 初始化 API 客户端
        initializeApi(token);
      }
      
      // 更新认证状态
      setAuthState({
        user: mockUser,
        isAuthenticated: true,
        isLoading: false
      });
      
      // 保存到本地存储
      localStorage.setItem('auth_user', JSON.stringify(mockUser));
      
      return { success: true };
      
    } catch (error) {
      console.error('登录失败:', error);
      return { success: false, message: '登录过程中发生错误' };
    }
  };

  // 登出方法
  const logout = () => {
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false
    });
    
    localStorage.removeItem('access_token');
    localStorage.removeItem(STORAGE_KEYS.TOKEN_OBJ);
    localStorage.removeItem('auth_user');
  };

  // 设置用户信息
  const setUser = (user: User | null) => {
    setAuthState(prev => ({
      ...prev,
      user,
      isAuthenticated: !!user
    }));
    
    if (user) {
      localStorage.setItem('auth_user', JSON.stringify(user));
    }
  };

  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    setUser
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// 自定义Hook - 使用认证上下文
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth必须在AuthProvider内部使用');
  }
  return context;
}

// 认证状态检查Hook
export function useAuthStatus() {
  const { isAuthenticated, isLoading } = useAuth();
  return { isAuthenticated, isLoading };
} 